import { Capacitor } from "@capacitor/core";
import type { ErrorEntry, ErrorTrackerConfig } from "../interfaces/ErrorTracker";
import { storage } from "./storage";
import { version } from "./constants";

class SimpleErrorTracker {
  private config: ErrorTrackerConfig = {
    enabled: true,
    maxStoredErrors: 100,
    enableConsoleLogging: true,
  };

  private storageKey = 'error_tracker_entries';

  constructor(config?: Partial<ErrorTrackerConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
  }


  private async getStoredErrors(): Promise<ErrorEntry[]> {
    try {
      const stored = await storage.get(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private async storeErrors(errors: ErrorEntry[]): Promise<void> {
    try {
      // Keep only the most recent errors within limit
      const limitedErrors = errors.slice(-this.config.maxStoredErrors);
      await storage.set(this.storageKey, JSON.stringify(limitedErrors));
    } catch (error) {
      console.warn('Failed to store errors:', error);
    }
  }

  private userId?: number;

  private formatMySQLDateTime(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  async captureError(
    error: Error | string,
    level: 'error' | 'warning' | 'info' = 'error',
    customData?: Record<string, any>
  ): Promise<void> {
    if (!this.config.enabled) return;

    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;

    const entry: ErrorEntry = {
      message,
      stack,
      level,
      user_id: this.userId,
      user_agent: navigator.userAgent,
      url: window.location.href,
      created_at: this.formatMySQLDateTime(),
      app_version: version,
      platform: Capacitor.getPlatform(),
      custom_data: { ...this.config.customData, ...customData },
    };

    // Log to console if enabled
    if (this.config.enableConsoleLogging) {
      console.error(`[ErrorTracker] ${level.toUpperCase()}: ${message}`, entry);
    }

    // Store locally
    try {
      const existingErrors = await this.getStoredErrors();
      existingErrors.push(entry);
      await this.storeErrors(existingErrors);
    } catch (storeError) {
      console.warn('Failed to store error entry:', storeError);
    }

    // Attempt to send immediately if endpoint is configured
    if (this.config.endpoint) {
      this.sendError(entry);
    }
  }

  private async sendError(entry: ErrorEntry): Promise<void> {
    if (!this.config.endpoint) return;

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      if (response.ok) {
        // Remove from local storage on successful send
        const errors = await this.getStoredErrors();
        const updatedErrors = errors.filter(e => 
          !(e.created_at === entry.created_at && e.message === entry.message)
        );
        await this.storeErrors(updatedErrors);
      }
    } catch (sendError) {
      // Silently fail - we'll retry later
      console.warn('Failed to send error to endpoint:', sendError);
    }
  }

  async getAllErrors(): Promise<ErrorEntry[]> {
    return this.getStoredErrors();
  }

  async retryStoredErrors(): Promise<void> {
    if (!this.config.endpoint) return;

    const errors = await this.getStoredErrors();
    // Send all stored errors (they're all unsent by definition)
    for (const error of errors) {
      await this.sendError(error);
    }
  }

  async clearErrors(): Promise<void> {
    await storage.set(this.storageKey, JSON.stringify([]));
  }

  updateConfig(newConfig: Partial<ErrorTrackerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  setUserId(userId: number): void {
    this.userId = userId;
  }
}

// Create singleton instance
export const errorTracker = new SimpleErrorTracker();

// Convenience functions
export const captureError = (error: Error | string, customData?: Record<string, any>) => 
  errorTracker.captureError(error, 'error', customData);

export const captureWarning = (message: string, customData?: Record<string, any>) => 
  errorTracker.captureError(message, 'warning', customData);

export const captureInfo = (message: string, customData?: Record<string, any>) => 
  errorTracker.captureError(message, 'info', customData);